import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  Eye,
  Calendar,
  User,
  Phone,
  MessageSquare,
  Sparkles,
  X
} from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import useToast from "../hooks/useToast";
import { getContactSubmissions } from "../services/contactService";

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function truncateText(text = "", maxLength = 60) {
  if (!text) return "--";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function ContactSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const { showErrorToast } = useToast();

  const loadSubmissions = async (pageArg = pagination.page, limitArg = pagination.limit) => {
    setIsLoading(true);
    const response = await getContactSubmissions({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setSubmissions(response.data.submissions || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load submissions");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadSubmissions(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openViewModal = (submission) => {
    setSelectedSubmission(submission);
  };

  const closeViewModal = () => {
    setSelectedSubmission(null);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Communication Module"
        title="Contact Submissions"
        description="View and manage messages received from the frontend website contact form."
        badge={`${pagination.total} Messages`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Inbound Messages</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Standardized listing of all user queries with quick view functionality.
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Mail className="h-5 w-5" />
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading submissions...
            </div>
          ) : submissions.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">User</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Subject</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Message</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Date</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {submissions.map((sub) => (
                        <tr key={sub.id} className="border-t border-slate-100 align-top hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-xs font-bold text-white uppercase">
                                {getInitials(sub.name)}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-semibold text-panel-ink truncate max-w-[150px]">{sub.name}</span>
                                <span className="text-xs text-panel-muted truncate max-w-[150px]">{sub.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm font-medium text-panel-ink leading-relaxed">
                            {truncateText(sub.subject, 40)}
                          </td>
                          <td className="px-5 py-4 text-sm text-panel-muted italic">
                            "{truncateText(sub.message)}"
                          </td>
                          <td className="px-5 py-4 text-sm text-panel-muted whitespace-nowrap">
                            {formatDate(sub.created_at)}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <Button type="button" size="sm" variant="outline" onClick={() => openViewModal(sub)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> messages
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-panel-muted">
                    Rows
                    <select
                      className="h-10 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-panel-ink outline-none"
                      value={pagination.limit}
                      onChange={(event) =>
                        setPagination((current) => ({
                          ...current,
                          page: 1,
                          limit: Number(event.target.value)
                        }))
                      }
                    >
                      {[10, 20, 50].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page - 1
                        }))
                      }
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Prev
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {pageNumbers.map((item, index) =>
                        item === "..." ? (
                          <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={item}
                            type="button"
                            size="sm"
                            variant={pagination.page === item ? "default" : "outline"}
                            onClick={() =>
                              setPagination((current) => ({
                                ...current,
                                page: item
                              }))
                            }
                          >
                            {item}
                          </Button>
                        )
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() =>
                        setPagination((current) => ({
                          ...current,
                          page: current.page + 1
                        }))
                      }
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No messages yet</p>
              <p className="mt-2 text-sm text-panel-muted">
                Messages submitted via the website contact form will appear here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedSubmission ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="max-h-[85dvh] w-full max-w-2xl overflow-y-auto rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-panel-ink truncate max-w-[400px]">
                    Message Detail
                  </h2>
                  <p className="text-sm text-panel-muted">Received on {formatDate(selectedSubmission.created_at)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <User className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Sender Name</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{selectedSubmission.name}</p>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Email Address</span>
                  </div>
                  <a href={`mailto:${selectedSubmission.email}`} className="text-panel-ink font-semibold hover:text-primary transition-colors">
                    {selectedSubmission.email}
                  </a>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <Phone className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Phone Number</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{selectedSubmission.phone || "Not provided"}</p>
                </div>

                <div className="space-y-1.5 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                  <div className="flex items-center gap-2 text-primary">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Submitted On</span>
                  </div>
                  <p className="text-panel-ink font-semibold">{formatDate(selectedSubmission.created_at)}</p>
                </div>
              </div>

              <div className="space-y-3 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100">
                <div className="flex items-center gap-2 text-primary">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Subject</span>
                </div>
                <p className="text-panel-ink font-bold text-lg leading-relaxed">
                  {selectedSubmission.subject}
                </p>
              </div>

              <div className="space-y-3 px-6 py-5 rounded-3xl bg-slate-50/50 border border-slate-100 relative">
                <div className="flex items-center gap-2 text-primary">
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">Full Message</span>
                </div>
                <div className="text-panel-ink leading-7 text-base bg-white/60 p-5 rounded-2xl border border-white whitespace-pre-wrap">
                  {selectedSubmission.message}
                </div>
              </div>

              <div className="pt-2">
                <Button type="button" className="w-full h-12 rounded-2xl text-base font-bold shadow-panel-sm" onClick={closeViewModal}>
                  Done Reading
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
